<script setup async>
import {fixtureFacts} from '~/utils/privateAccess';
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import PlayerCard from '@/components/players/PlayerCard.vue';
import {useHeadService} from '~/composables/useHeadService.js';
import {useAuthService} from '~/composables/useAuthService.js';
import {useMessaging} from '~/composables/useMessaging.js';
import FixtureCell from "~/components/fixtures/FixtureCell.vue";
import FixtureWhenAndWhere from "~/components/fixtures/FixtureWhenAndWhere.vue";
import HandicapMatchBanner from "~/components/handicaps/HandicapMatchBanner.vue";
import HandicapChangesTable from "~/components/handicaps/HandicapChangesTable.vue";
import {createError} from "nuxt/app";
import FrontendBreadcrumbs from "~/components/navigation/FrontendBreadcrumbs.vue";
import ShortLinkShareWrapper from "~/components/share/ShortLinkShareWrapper.vue";
import LadderView from "~/components/ladders/LadderView.vue";
import StandingsTable from "~/components/leagues/StandingsTable.vue";
import ClickableCell from "~/components/lists/ClickableCell.vue";
import TeamCard from "~/components/teams/TeamCard.vue";
import PostMatchSummary from "~/components/scoring/PostMatchSummary.vue";
import FixtureLadderPositionChange from "~/components/fixtures/FixtureLadderPositionChange.vue";
import {useSignalR} from "~/composables/useSignalR.js";

definePageMeta({
  name: 'FixtureDetail',
  path: '/fixtures/:fixtureId',
});

const route = useRoute();
const router = useRouter();
const fixtureId = route.params.fixtureId;
const {user} = useAuthService();
const {outputDateFromDb} = useCrudConverters();

// Fetch fixture data
const {
  item: fixture,
  getItem: getFixture,
  itemPending: fixturePending,
  hasItemErrors
} = useCrudApi("api/v2/fixtures");

await getFixture(fixtureId);

if (hasItemErrors.value) {
  throw createError({
    statusCode: 404,
    message: 'Fixture not found. Has it been deleted?'
  });
}

// ---- fixture type + state ----
// tournamentStageType: 1 = group, 2 = knockout, 3 = third-place playoff.
const {shortDateTime} = useLocalisedDates();
const isLeagueFixture = computed(() => !!fixture.value?.leagueCycleDivisionId);
const isLadderFixture = computed(() => !!fixture.value?.ladderId);
const isTournamentFixture = computed(() => !!fixture.value?.tournamentStageId);
const isTournamentGroupFixture = computed(() => fixture.value?.tournamentStageType === 1);
const hasResult = computed(() => fixture.value?.homeTeamResult != null && fixture.value?.awayTeamResult != null);


// Squads bigger than a pair get their full player lists laid out on the page
// — the generated team labels ("Team A") say nothing on their own.
const bigSquads = computed(() =>
    (fixture.value?.homeTeam?.players?.length ?? 0) > 2
    || (fixture.value?.awayTeam?.players?.length ?? 0) > 2);
const squadWon = (side) => {
  if (!hasResult.value) return false;
  const mine = side === 'home' ? fixture.value?.homeTeamResult : fixture.value?.awayTeamResult;
  return mine === 'W';
};

// Points are awarded ONLY by league divisions and tournament GROUP stages.
// Knockout fixtures DO carry points in the data, so the explicit group check
// (tournamentStageType === 1) is required — a points-present guard alone would
// wrongly show them for a semi-final. Ladder / friendly award none.
const hasPoints = computed(() => fixture.value?.homeTeamPoints != null || fixture.value?.awayTeamPoints != null);
const showPoints = computed(() =>
    hasResult.value && (isLeagueFixture.value || isTournamentGroupFixture.value) && hasPoints.value);

const isFullyApproved = computed(() =>
    fixture.value?.homeTeamApprovedAt != null && fixture.value?.awayTeamApprovedAt != null);

// The side still pending approval — drives the "Points apply once X approves" note.
const firstName = (name) => (name || '').trim().split(/\s+/).filter(Boolean)[0] || name || 'your opponent';
const pendingTeam = computed(() => {
  const f = fixture.value;
  if (!f) return null;
  if (f.homeTeamApprovedAt == null) return f.homeTeam;
  if (f.awayTeamApprovedAt == null) return f.awayTeam;
  return null;
});
const approverName = computed(() => {
  const t = pendingTeam.value;
  if (!t) return 'your opponent';
  const isMe = (t.players || []).some((p) => p.userId === user.value?.id);
  return isMe ? 'you' : firstName(t.name);
});
const approveVerb = computed(() => (approverName.value === 'you' ? 'approve' : 'approves'));

// ---- opponent (for "Message" action) ----
const opponentTeam = computed(() => {
  const f = fixture.value;
  if (!f) return null;
  const inHome = (f.homeTeam?.players || []).some((p) => p.userId === user.value?.id);
  const inAway = (f.awayTeam?.players || []).some((p) => p.userId === user.value?.id);
  if (inHome) return f.awayTeam;
  if (inAway) return f.homeTeam;
  return null;
});
const opponentUserId = computed(() => opponentTeam.value?.singlePlayer?.userId ?? null);
const opponentFirstName = computed(() => firstName(opponentTeam.value?.name));

const {createConversation} = useMessaging();
const messaging = ref(false);
const messageOpponent = async () => {
  if (!opponentUserId.value || messaging.value) return;
  messaging.value = true;
  try {
    const conversation = await createConversation([opponentUserId.value], 1, null);
    await router.push({query: {...route.query, conversationId: conversation.id, messaging: 'open'}});
  } catch (e) {
    // surfaced by the messaging layer
  } finally {
    messaging.value = false;
  }
};

// ---- head / meta ----
const {setMeta} = useHeadService();
const fixtureContext = computed(() => {
  const f = fixture.value;
  if (!f) return null;
  const parts = [];
  if (f.ladderName) parts.push(f.ladderName);
  else if (f.leagueName) {
    parts.push(f.leagueName);
    if (f.leagueCycleDivisionName) parts.push(f.leagueCycleDivisionName);
  } else if (f.tournamentName) {
    parts.push(f.tournamentName);
    if (f.tournamentStageName) parts.push(f.tournamentStageName);
  } else if (f.americanoName) {
    parts.push(f.americanoName);
    if (f.americanoCycleName) parts.push(f.americanoCycleName);
    if (f.americanoRound) parts.push(`Round ${f.americanoRound}`);
  } else if (f.meetName) {
    parts.push(f.meetName);
  }
  return parts.length ? parts.join(' · ') : 'Friendly';
});
// A private match carries no names: a plain title and the default image (PRIVACY_PLAN.md §4.4).
const fixturePrivate = computed(() => !!fixture.value?.privateToViewer);
usePrivatePage(fixturePrivate);
const fixtureContextName = computed(() => {
  const f = fixture.value ?? {};
  return f.leagueName || f.ladderName || f.tournamentName || f.americanoName || f.meetName || '';
});
if (fixturePrivate.value) {
  setMeta('Match on Tap Leagues', 'Match details on Tap Leagues');
} else {
  setMeta(
      `${fixture.value?.homeTeam?.name ?? 'TBC'} vs ${fixture.value?.awayTeam?.name ?? 'TBC'}`,
      'Match details on Tap Leagues',
      'Fixture',
  );
}

// ---- context standings (by type) ----
const {item: ladder, getItem: getLadder} = useCrudApi("api/v2/ladders");
if (isLadderFixture.value) {
  await getLadder(fixture.value.ladderId);
}
const lcd = ref(null);
const {item: lcdItem, getItem: getLcd} = useCrudApi("api/v2/leagueCycleDivisions");
if (isLeagueFixture.value) {
  await getLcd(fixture.value.leagueCycleDivisionId);
  lcd.value = lcdItem.value;
}

// Tournament GROUP fixtures show the group's standings (the stage's teams are
// already standings-shaped: position/totalPoints/bonusPoints/fixtures/players).
// Knockout fixtures have no standings context (matches the tournament page).
const stage = ref(null);
const {item: stageItem, getItem: getStage} = useCrudApi("api/v2/tournamentStages");
if (isTournamentGroupFixture.value) {
  await getStage(fixture.value.tournamentStageId);
  stage.value = stageItem.value;
}

const highlightedTeamIds = computed(() =>
    [fixture.value?.homeTeamId, fixture.value?.awayTeamId].filter(Boolean));

const hasLadderPositions = computed(() => (fixture.value?.ladderPositions?.length ?? 0) > 0);
const teamNameById = (teamId) => {
  const f = fixture.value;
  if (f?.homeTeamId === teamId) return f.homeTeam?.name ?? 'TBC';
  if (f?.awayTeamId === teamId) return f.awayTeam?.name ?? 'TBC';
  return 'Team';
};
// Americano handicap changes are per PLAYER (the row's teamId is the player's solo team) —
// name the player; team competitions fall back to the fixture's team names.
const handicapChangeName = (change) => {
  if (change.playerId) {
    const players = [...(fixture.value?.homeTeam?.players ?? []), ...(fixture.value?.awayTeam?.players ?? [])];
    const p = players.find((x) => x.id === change.playerId);
    if (p) return p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ');
  }
  return teamNameById(change.teamId);
};

// ---- live updates ----
const {addListener, removeLocalListenersWithEvent} = useListenerService();
const {on, off} = useSignalR();
// A result change (enter, edit, approve, clear) moves the table under it too, so
// refetch the standings alongside the fixture rather than leaving them stale.
const refreshStandings = async () => {
  try {
    if (isLadderFixture.value) await getLadder(fixture.value.ladderId);
    if (isLeagueFixture.value) { await getLcd(fixture.value.leagueCycleDivisionId); lcd.value = lcdItem.value; }
    if (isTournamentGroupFixture.value) { await getStage(fixture.value.tournamentStageId); stage.value = stageItem.value; }
  } catch {
    // the fixture itself updated; a stale table is not worth an error
  }
};
const onFixtureUpdated = (updatedFixture) => {
  if (updatedFixture.id === fixtureId) {
    fixture.value = updatedFixture;
    refreshStandings();
  }
};
const onFixtureSignal = async () => {
  await getFixture(fixtureId);
  refreshStandings();
};
onMounted(() => {
  addListener('fixtureUpdated', onFixtureUpdated);
  on(fixture.value.id, onFixtureSignal);
});
onBeforeUnmount(() => {
  removeLocalListenersWithEvent('fixtureUpdated');
  off(fixture.value.id, onFixtureSignal);
});
</script>

<template>
  <base-page>
    <!-- Members only for this viewer (PRIVACY_PLAN.md §4.1): the stub keeps the competition and the date -->
    <v-container v-if="!fixturePending && fixturePrivate" class="large">
      <frontend-breadcrumbs :fixture="fixture" class="fxp-breadcrumbs"/>
      <div class="fxp-head">
        <div class="hgroup">
          <h1>Match</h1>
          <div class="fxp-sub" v-if="fixtureContext">{{ fixtureContext }}</div>
        </div>
      </div>
      <private-notice reason="fixture" :name="fixtureContextName" :club-name="fixture?.clubName"
                      :facts="fixtureFacts(fixture, d => outputDateFromDb(d, 'd MMM yyyy, HH:mm'))"/>
    </v-container>
    <v-container v-else-if="!fixturePending">
      <frontend-breadcrumbs :fixture="fixture" class="fxp-breadcrumbs"/>

      <!-- title + context. Messaging is the topbar icon — only Share here. -->
      <div class="fxp-head">
        <div class="hgroup">
          <h1>Match</h1>
          <div class="fxp-sub" v-if="fixtureContext">{{ fixtureContext }}</div>
        </div>
        <div class="fxp-actions">
          <!-- Live scoring is an optional enhancement — post-hoc entry stays the primary path -->
          <v-btn
              v-if="!hasResult && fixture.supportsLiveScoring"
              variant="outlined"
              size="small"
              rounded="pill"
              prepend-icon="mdi-scoreboard-outline"
              :to="`/fixtures/${fixtureId}/live`"
          >Live score</v-btn>
          <short-link-share-wrapper>
            <template #default="{ click, loading: shareLoading }">
              <v-btn
                  variant="outlined"
                  size="small"
                  rounded="pill"
                  prepend-icon="mdi-share-variant"
                  :loading="shareLoading"
                  @click="click"
              >Share</v-btn>
            </template>
          </short-link-share-wrapper>
        </div>
      </div>

      <v-row>
        <!-- ════════ MAIN COLUMN ════════ -->
        <v-col cols="12" md="8">
          <!-- Result card — reuses FixtureCell (meta chips, vs-row, games, and the
               foot that already drives edit/record/approve/suggest/cancel/clear/
               delete via the BasePage route-query dialogs). -->
          <fixture-cell :fixture="fixture" size="large" :navigable="false" :bordered="true" class="mb-0"/>

          <!-- Squads bigger than a pair: every player, laid out per side — the
               team label alone ("Team A") says nothing. -->
          <template v-if="bigSquads">
            <div class="tl-section-head"><h2>Players</h2></div>
            <div class="tl-card fxp-squads">
              <div class="fxp-squad-col" :class="{'fxp-squad-col--won': squadWon('home')}">
                <div v-for="p in fixture.homeTeam?.players ?? []" :key="p.id" class="fxp-squad-row">
                  <player-card :player="p"/>
                </div>
              </div>
              <div class="fxp-squad-col" :class="{'fxp-squad-col--won': squadWon('away')}">
                <div v-for="p in fixture.awayTeam?.players ?? []" :key="p.id" class="fxp-squad-row">
                  <player-card :player="p"/>
                </div>
              </div>
            </div>
          </template>

          <fixture-when-and-where :fixture="fixture"/>

          <!-- Match detail — events captured via live scoring or manual tagging -->
          <post-match-summary :fixture="fixture"/>

          <!-- Points — league division & tournament GROUP only (knockout/ladder/friendly: hidden) -->
          <template v-if="showPoints">
            <div class="tl-section-head"><h2>Points from this match</h2></div>
            <div class="tl-card">
              <div class="fxp-points-row">
                <team-card :team="fixture.homeTeam" size="small" :show-squash-level="false" class="flex-grow-1"/>
                <span v-if="fixture.homeTeamBonus" class="fxp-points-bonus">counts toward bonus</span>
                <span class="fxp-points-val">{{ fixture.homeTeamPoints ?? '–' }}</span>
              </div>
              <div class="fxp-points-divider"></div>
              <div class="fxp-points-row">
                <team-card :team="fixture.awayTeam" size="small" :show-squash-level="false" class="flex-grow-1"/>
                <span v-if="fixture.awayTeamBonus" class="fxp-points-bonus">counts toward bonus</span>
                <span class="fxp-points-val">{{ fixture.awayTeamPoints ?? '–' }}</span>
              </div>
            </div>
            <div v-if="!isFullyApproved" class="fxp-note">
              Points apply once {{ approverName }} {{ approveVerb }} the result.
            </div>
          </template>

          <!-- Match insight — fixture.analysis (league opt-in) -->
          <template v-if="fixture.analysis">
            <div class="tl-section-head"><h2>Match insight</h2></div>
            <div class="ai-card">
              <div class="ai-head"><v-icon size="16" icon="mdi-creation"/>AI analysis</div>
              <p class="mb-0">{{ fixture.analysis }}</p>
            </div>
          </template>

          <!-- Team handicaps — the resolved start for this match and what the result did to the handicaps -->
          <template v-if="fixture.handicapStart && (!fixture.handicapStart.isLevel || fixture.handicapChanges?.length)">
            <div class="tl-section-head"><h2>Handicap</h2></div>
            <handicap-match-banner :start="fixture.handicapStart"/>
            <template v-if="fixture.handicapChanges?.length">
              <div class="fxp-sub-head">Handicap changes from this result</div>
              <handicap-changes-table :changes="fixture.handicapChanges" :settings="fixture.handicapSettings"
                                      :noun="bigSquads || (fixture.homeTeam?.players?.length ?? 1) > 1 ? 'Team' : 'Player'"
                                      :name-for="handicapChangeName"/>
            </template>
          </template>

          <!-- Ladder position changes — ladder fixtures with recorded movement -->
          <template v-if="hasLadderPositions">
            <div class="tl-section-head"><h2>Ladder position changes</h2></div>
            <div class="tl-card">
              <div v-for="(pos, i) in fixture.ladderPositions" :key="pos.teamId"
                   class="fxp-pos-row" :class="{ 'fxp-pos-row--divider': i > 0 }">
                <span class="fxp-pos-name">{{ teamNameById(pos.teamId) }}</span>
                <fixture-ladder-position-change :ladder-position-change="pos"/>
              </div>
            </div>
          </template>

          <!-- Standings (competition info) — league table / ladder, both fixture
               teams highlighted. Tournament/friendly load no standings. -->
          <template v-if="isLeagueFixture && lcd">
            <!-- No squash ratings here: this page loads the division and stage
                 directly, and neither payload carries its competition's sport, so
                 the gate can't be answered without another request. Hidden is the
                 correct answer to "I don't know what sport this is". -->
            <div class="tl-section-head"><h2>{{ fixture.leagueCycleDivisionName || 'Standings' }}</h2></div>
            <standings-table :division="lcd" :item-limit="100" :players-per-team="fixture.leagueCycle?.playersPerTeam ?? (bigSquads ? 2 : 1)" :highlight-team-ids="highlightedTeamIds" :highlight-me="false"/>
          </template>
          <template v-else-if="isLadderFixture && ladder && ladder.teams">
            <div class="tl-section-head"><h2>{{ fixture.ladderName || 'Standings' }}</h2></div>
            <div class="tl-card">
              <ladder-view :ladder="ladder" :simple="true" :highlighted-team-ids="highlightedTeamIds"/>
            </div>
          </template>
          <!-- tournament GROUP fixtures show the group standings (knockout: none) -->
          <template v-else-if="isTournamentGroupFixture && stage">
            <div class="tl-section-head"><h2>{{ fixture.tournamentStageName || 'Group' }}</h2></div>
            <standings-table :division="stage" :show-bonus="false" :item-limit="100" :highlight-team-ids="highlightedTeamIds" :highlight-me="false"/>
          </template>
        </v-col>

        <!-- ════════ SIDE RAIL ════════ -->
        <v-col cols="12" md="4">
          <div class="fxp-side-sticky">
            <div class="tl-section-head mt-0"><h2>Actions</h2></div>
            <!-- Each ClickableCell is its own bordered card (like Home) — no outer
                 wrapper, which would double-border the list. -->
            <clickable-cell
                v-if="opponentUserId"
                tone="neutral" icon="mdi-chat-outline"
                :title="`Message ${opponentFirstName}`"
                :loading="messaging"
                :click="messageOpponent"
            />
            <clickable-cell
                v-if="fixture.ladderId && fixture.ladderChallengeId"
                tone="neutral" icon="mdi-handshake-outline" title="View challenge"
                :to="{ path: `/ladders/${fixture.ladderId}/challenges/${fixture.ladderChallengeId}` }"
            />
            <clickable-cell
                v-if="fixture.leagueId"
                tone="league" icon="mdi-table" :title="fixture.leagueName || 'Open league'"
                :subtitle="[fixture.leagueCycleName, fixture.leagueCycleDivisionName].filter(Boolean).join(' · ')"
                :to="{ name: 'LeagueDetail', params: { leagueId: fixture.leagueId } }"
            />
            <clickable-cell
                v-if="fixture.ladderId"
                tone="ladder" icon="mdi-ladder" :title="fixture.ladderName || 'Open ladder'"
                :to="{ name: 'LadderDetail', params: { ladderId: fixture.ladderId } }"
            />
            <clickable-cell
                v-if="fixture.tournamentId"
                tone="tournament" icon="mdi-trophy" :title="fixture.tournamentName || 'Open tournament'"
                :to="{ name: 'TournamentDetail', params: { tournamentId: fixture.tournamentId } }"
            />
            <clickable-cell
                v-if="fixture.meetId && fixture.meetOccurrenceId"
                tone="meet" icon="mdi-handshake-outline" :title="fixture.meetName || 'Open meet'"
                :subtitle="fixture.scheduledAt ? 'Date: ' + shortDateTime(fixture.scheduledAt) : ''"
                :to="{ name: 'MeetOccurrenceDetail', params: { meetId: fixture.meetId, occurrenceId: fixture.meetOccurrenceId } }"
            />
            <clickable-cell
                v-if="fixture.americanoId"
                tone="americano" icon="mdi-account-switch" :title="fixture.americanoName || 'Open Americano league'"
                :subtitle="[fixture.americanoCycleName, fixture.americanoCycleDivisionName].filter(Boolean).join(' · ')"
                :to="{ name: 'AmericanoDetail', params: { americanoId: fixture.americanoId, cycleId: fixture.americanoCycleId } }"
            />
            <clickable-cell
                v-if="fixture.clubName && (fixture.clubSlug || fixture.clubId)"
                tone="neutral" icon="mdi-account-group" :title="fixture.clubName"
                :to="{ name: 'ClubDetail', params: { clubId: fixture.clubSlug || fixture.clubId } }"
            />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <div class="fxp-loading" v-else>
      <v-progress-circular indeterminate color="primary" size="64"/>
      <div class="mt-4 text-medium-emphasis">Loading match details...</div>
    </div>
  </base-page>
</template>

<style scoped lang="scss">
.fxp-breadcrumbs {
  background: transparent !important;
  padding: 0 !important;
  margin-bottom: 12px;
}

/* page head (mockup .page-head) */
.fxp-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 6px 0 18px;

  .hgroup {
    flex: 1 1 auto;
    min-width: 0;
  }

  h1 {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.5px;
  }
}

.fxp-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 4px;
  line-height: 1.6;
}

.tl-section-head {
  margin-top: 20px;
}

/* Result card team names: at size="large" FixtureCell renders the name at 24px
   (PlayerCard for single players → an inline-styled .line-clamp-2 span;
   TeamCard for multi-player → .team-card-name--*). The design wants a compact
   name under the big score, so cap both here (page-local) — keeps the 45px
   avatar + 30px score, just shrinks the name like the mockup. */
:deep(.fixture-team-name .line-clamp-2),
:deep(.fixture-team-name .text-truncate),
:deep(.fixture-team-name [class*="team-card-name"]) {
  font-size: 13.5px !important;
  line-height: 1.35 !important;
}

/* points snapshot rows (mockup .card.snapshot) */
.fxp-points-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
}

.fxp-points-divider {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.fxp-points-bonus {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  white-space: nowrap;
}

.fxp-points-val {
  font-size: 13px;
  font-weight: 700;
  min-width: 18px;
  text-align: right;
}

.fxp-note {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin: 8px 2px 0;
}

/* ladder position change rows */
.fxp-sub-head {
  margin: 14px 0 6px;
  font-size: 12px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
}

.fxp-pos-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;

  &--divider {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

.fxp-pos-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* AI insight card (mockup .ai-card) */
.ai-card {
  border: 1px solid #E3DBF4;
  background: linear-gradient(160deg, #FBFAFE 0%, #F6F2FC 100%);
  border-radius: 14px;
  padding: 14px 16px;
}

.ai-card .ai-head {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  color: #7C5CC4;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
}

.ai-card p {
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.65;
  white-space: pre-line;
}

/* sticky side rail on desktop (matches Home) */
.fxp-side-sticky {
  @media (min-width: 960px) {
    position: sticky;
    top: 80px;
  }
}

.fxp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
}

/* Squads bigger than a pair: both sides' full player lists (winner tinted). */
.fxp-squads {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px;
}

.fxp-squad-col {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  padding: 6px 10px;

  &--won {
    border-color: rgb(var(--v-theme-success));
  }
}

.fxp-squad-row {
  padding: 4px 0;

  + .fxp-squad-row {
    border-top: 1px solid rgb(var(--v-theme-borderLight));
  }
}

@media (max-width: 600px) {
  .fxp-squads {
    grid-template-columns: 1fr;
  }
}
</style>
